import type { Ref } from 'vue';
import type { BlockEditorBounds } from '#layers/block-engine/composables/block-editor/bounds';
import type { BlockEditorChildParentMap } from '#layers/block-engine/composables/block-editor/child-parent-map';
import type { BlockEditorInsertion } from '#layers/block-engine/composables/block-editor/insertion';
import type { BlockEditorMoveBlock } from '#layers/block-engine/composables/block-editor/move';
import type { BlockSerialized } from '#layers/block-engine/types/block';
import type { OverrideBlockDragConfig, OverrideBlockDragContext, OverrideBlockDragDropContext } from '#layers/block-engine/utils/block/api/drag';
import type { BlockBounds } from './bounds';
import { useEventListener } from '@vueuse/core';
import { computed, reactive, readonly, ref, watch } from 'vue';
import { slotOutletBlockId } from '#layers/block-engine/utils/block/const';
import { isBlockSerializedChild, isBlockSerializedSlotOutlet } from '#layers/block-engine/utils/block/editor';
import { pathHasCanvas } from './overlay/utils';

export type DraggedBlockBounds = Pick<BlockBounds, 'x' | 'y' | 'width' | 'height' | 'horizontal'>;
export interface HoverDragTargetBounds {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface DraggedBlockPointerOffset {
	x: number;
	y: number;
}

export interface DraggedBlockOrigin {
	parentChildId?: string;
	slotName?: string;
	index: number;
}

export interface DraggedBlock {
	source: 'canvas' | 'tree';
	blockId: typeof slotOutletBlockId | string;
	childId?: string;
	origin?: DraggedBlockOrigin;
	bounds?: DraggedBlockBounds;
	pointerOffset?: DraggedBlockPointerOffset;
}

interface DragBlockTarget {
	id: string;
	childId?: string;
	slotName?: string;
	index: number;
}

interface HoverDragTargetState {
	ownerId: string;
	target: DragBlockTarget;
	bounds?: HoverDragTargetBounds;
	hysteresisPx: number;
}

/**
 * Represents a block that has captured the drag
 */
export interface DragCaptureState {
	/**
	 * The child ID of the block that captured the drag
	 */
	childId: string;
	/**
	 * The override config from the block
	 */
	config: OverrideBlockDragConfig;
	/**
	 * The bounds of the capturing block
	 */
	bounds: { x: number; y: number; width: number; height: number };
}

interface DragGestureState {
	pointerDown: boolean;
	dragStarted: boolean;
	dropCompleted: boolean;
	suppressPointerRelease: boolean;
	suppressClick: boolean;
}

export function useBlockEditorDrag({
	blockData,
	insertion,
	moveBlock,
	bounds,
	childParentMap,
}: {
	blockData: Ref<BlockSerialized | null>;
	insertion: BlockEditorInsertion;
	moveBlock: BlockEditorMoveBlock;
	bounds: BlockEditorBounds;
	childParentMap: BlockEditorChildParentMap;
}) {
	/**
	 * Block being dragged
	 */
	const draggedBlock = ref<DraggedBlock | null>(null);
	const hoverDragTargetState = ref<HoverDragTargetState | null>(null);
	const hoverDragTarget = computed(() => hoverDragTargetState.value?.target ?? null);
	const dragging = computed(() => !!draggedBlock.value);

	/**
	 * Block that has captured the drag (for overrideBlockDrag)
	 */
	const dragCapture = ref<DragCaptureState | null>(null);

	/**
	 * Whether the drag is currently captured by a block
	 */
	const isCaptured = computed(() => dragCapture.value !== null);
	const gesture = reactive<DragGestureState>({
		pointerDown: false,
		dragStarted: false,
		dropCompleted: false,
		suppressPointerRelease: false,
		suppressClick: false,
	});

	function resetDropSuppression() {
		if (!gesture.suppressPointerRelease && !gesture.suppressClick) {
			gesture.dropCompleted = false;
		}
	}

	function beginPointerGesture() {
		gesture.pointerDown = true;
		gesture.dragStarted = false;
		gesture.dropCompleted = false;
		gesture.suppressPointerRelease = false;
		gesture.suppressClick = false;
	}

	function resetPointerGesture() {
		gesture.pointerDown = false;
		gesture.dragStarted = false;
		gesture.dropCompleted = false;
		gesture.suppressPointerRelease = false;
		gesture.suppressClick = false;
	}

	function cancelPointerGesture() {
		if (gesture.dragStarted) {
			return;
		}

		resetPointerGesture();
	}

	function markDropCompleted() {
		const shouldSuppress = gesture.dragStarted;

		gesture.pointerDown = false;
		gesture.dragStarted = false;
		gesture.dropCompleted = shouldSuppress;
		gesture.suppressPointerRelease = shouldSuppress;
		gesture.suppressClick = shouldSuppress;
	}

	function consumeSuppressedPointerRelease() {
		if (!gesture.suppressPointerRelease) {
			return false;
		}

		gesture.suppressPointerRelease = false;
		resetDropSuppression();
		return true;
	}

	function consumeSuppressedClick() {
		if (!gesture.suppressClick) {
			return false;
		}

		gesture.suppressClick = false;
		resetDropSuppression();
		return true;
	}

	function clearActiveDrag() {
		dragCapture.value = null;
		draggedBlock.value = null;
		clearHoverDragTarget();
	}

	function getDraggedBlockOrigin(childId: string): DraggedBlockOrigin | undefined {
		if (!blockData.value) {
			return undefined;
		}

		const parent = childParentMap.value.get(childId);

		if (parent) {
			const parentChild = blockData.value.children?.[parent.parentId];

			if (isBlockSerializedChild(parentChild)) {
				const index = parentChild.slots[parent.slot]?.indexOf(childId) ?? -1;

				if (index !== -1) {
					return {
						parentChildId: parent.parentId,
						slotName: parent.slot,
						index,
					};
				}
			}
		}

		const rootIndex = blockData.value.rootChildren?.indexOf(childId) ?? -1;

		if (rootIndex !== -1) {
			return {
				index: rootIndex,
			};
		}

		return undefined;
	}

	function getDraggedBlockBounds(childId: string): DraggedBlockBounds | undefined {
		const currentBounds = [...bounds.bounds.value.values()].find((entry) => entry.childId === childId);

		if (!currentBounds) {
			return undefined;
		}

		return {
			x: currentBounds.x,
			y: currentBounds.y,
			width: currentBounds.width,
			height: currentBounds.height,
			horizontal: currentBounds.horizontal,
		};
	}

	function startDrag(options: {
		source: DraggedBlock['source'];
		blockId: DraggedBlock['blockId'];
		childId?: string;
		bounds?: DraggedBlockBounds;
		mouse?: { x: number; y: number };
		pointerOffset?: DraggedBlockPointerOffset;
	}) {
		clearHoverDragTarget();
		dragCapture.value = null;

		const resolvedBounds = options.bounds ?? (options.childId ? getDraggedBlockBounds(options.childId) : undefined);
		const resolvedPointerOffset = options.source === 'canvas'
			? options.pointerOffset ?? (options.mouse && resolvedBounds
				? {
						x: options.mouse.x - resolvedBounds.x,
						y: options.mouse.y - resolvedBounds.y,
					}
				: undefined)
			: undefined;

		draggedBlock.value = {
			...options,
			origin: options.childId ? getDraggedBlockOrigin(options.childId) : undefined,
			bounds: resolvedBounds,
			pointerOffset: resolvedPointerOffset,
		};
	}

	function setHoverDragTarget(options: {
		ownerId: string;
		target: DragBlockTarget;
		bounds?: HoverDragTargetBounds;
		hysteresisPx?: number;
	}) {
		hoverDragTargetState.value = {
			ownerId: options.ownerId,
			target: options.target,
			bounds: options.bounds,
			hysteresisPx: options.hysteresisPx ?? 0,
		};
	}

	function clearHoverDragTarget(ownerId?: string) {
		if (!ownerId || hoverDragTargetState.value?.ownerId === ownerId) {
			hoverDragTargetState.value = null;
		}
	}

	function hoverDragTargetOwnedBy(ownerId: string) {
		return hoverDragTargetState.value?.ownerId === ownerId;
	}

	function hoverDragTargetContainsPoint(ownerId: string, x: number, y: number) {
		const currentTarget = hoverDragTargetState.value;

		if (!currentTarget || currentTarget.ownerId !== ownerId || !currentTarget.bounds) {
			return false;
		}

		const { bounds, hysteresisPx } = currentTarget;

		return (
			x >= bounds.x - hysteresisPx
			&& x <= bounds.x + bounds.width + hysteresisPx
			&& y >= bounds.y - hysteresisPx
			&& y <= bounds.y + bounds.height + hysteresisPx
		);
	}

	watch(draggedBlock, (value, oldValue) => {
		if (value && !oldValue) {
			gesture.pointerDown = true;
			gesture.dragStarted = true;
			gesture.dropCompleted = false;
		}
	}, {
		flush: 'sync',
	});

	/**
	 * Try to capture the drag with a block's overrideBlockDrag config.
	 * Returns true if the drag was captured.
	 */
	function tryCaptureDrag(
		childId: string,
		config: OverrideBlockDragConfig,
		bounds: { x: number; y: number; width: number; height: number },
		mouse: { x: number; y: number },
	): boolean {
		if (!draggedBlock.value) return false;

		// Don't capture our own drag
		if (draggedBlock.value.childId === childId) return false;

		const context: OverrideBlockDragContext = {
			draggedBlock: draggedBlock.value,
			childId,
			mouse,
			bounds,
		};

		const result = config.onDragOver(context);

		if (result.captured) {
			// If we're switching from another capture, call onDragLeave on the old one
			if (dragCapture.value && dragCapture.value.childId !== childId) {
				const oldContext: OverrideBlockDragContext = {
					draggedBlock: draggedBlock.value,
					childId: dragCapture.value.childId,
					mouse,
					bounds: dragCapture.value.bounds,
				};
				dragCapture.value.config.onDragLeave?.(oldContext);
			}

			dragCapture.value = { childId, config, bounds };
			// Clear the normal hover target since we're capturing
			clearHoverDragTarget();
			return true;
		}

		return false;
	}

	/**
	 * Update the capture state (called on mouse move when captured)
	 */
	function updateCapture(
		mouse: { x: number; y: number },
		bounds: { x: number; y: number; width: number; height: number },
	) {
		if (!dragCapture.value || !draggedBlock.value) return;

		dragCapture.value.bounds = bounds;

		const context: OverrideBlockDragContext = {
			draggedBlock: draggedBlock.value,
			childId: dragCapture.value.childId,
			mouse,
			bounds,
		};

		// Check if still captured
		const result = dragCapture.value.config.onDragOver(context);

		if (!result.captured) {
			releaseDragCapture(mouse);
		}
	}

	/**
	 * Release the drag capture (called when leaving bounds or drag ends)
	 */
	function releaseDragCapture(mouse: { x: number; y: number }) {
		if (!dragCapture.value || !draggedBlock.value) return;

		const context: OverrideBlockDragContext = {
			draggedBlock: draggedBlock.value,
			childId: dragCapture.value.childId,
			mouse,
			bounds: dragCapture.value.bounds,
		};

		dragCapture.value.config.onDragLeave?.(context);
		dragCapture.value = null;
	}

	useEventListener('mouseup', (event) => {
		if (draggedBlock.value) {
			try {
				// If drag is captured, call the capturing block's onDrop
				if (dragCapture.value) {
					const context: OverrideBlockDragDropContext = {
						draggedBlock: draggedBlock.value,
						childId: dragCapture.value.childId,
						mouse: { x: event.clientX, y: event.clientY },
						bounds: dragCapture.value.bounds,
						altKey: event.altKey,
					};
					dragCapture.value.config.onDrop(context);
					markDropCompleted();
					clearActiveDrag();
					return;
				}

				if (hoverDragTarget.value) {
					const target = hoverDragTarget.value;

					if (target) {
						if (draggedBlock.value.childId) {
							if (event.altKey) {
								if (blockData.value) {
									const child = blockData.value.children?.[draggedBlock.value.childId];

									if (child) {
										let blockId: string;

										if (isBlockSerializedChild(child)) {
											blockId = child.blockId;
										}
										else if (isBlockSerializedSlotOutlet(child)) {
											blockId = slotOutletBlockId;
										}
										else {
											throw new Error('Invalid dragged block');
										}

										insertion.add({
											blockId,
											target: {
												...target,
												slotName: isBlockSerializedSlotOutlet(child) ? child.slotName : undefined,
											},
											props: child.props,
										});
									}
								}
							}
							else {
								moveBlock({
									childId: draggedBlock.value.childId,
									to: {
										parentChildId: target.childId,
										slot: target.slotName,
										index: target.index,
									},
								});
							}
						}
						else {
							insertion.add({
								blockId: draggedBlock.value.blockId,
								target,
							});
						}
					}
				}
				else {
					// Move to end of root if mouse position is below the bottom-most block
					const mouseY = event.clientY;

					// Find the bottom-most block position
					let maxBottom = 0;

					for (const [, blockBounds] of bounds.bounds.value) {
						const bottom = blockBounds.y + blockBounds.height;

						if (bottom > maxBottom) {
							maxBottom = bottom;
						}
					}

					// Only add to root if mouse is below the bottom-most block while still over the canvas.
					if (mouseY > maxBottom && pathHasCanvas(event.composedPath() as Array<EventTarget>)) {
						const rootChildrenLength = blockData.value?.rootChildren?.length ?? 0;

						if (draggedBlock.value.childId) {
							moveBlock({
								childId: draggedBlock.value.childId,
								to: {
									parentChildId: undefined,
									slot: undefined,
									index: rootChildrenLength,
								},
							});
						}
						else {
							insertion.add({
								blockId: draggedBlock.value.blockId,
								target: {
									id: 'root',
									childId: undefined,
									index: rootChildrenLength,
								},
							});
						}
					}
				}
			}
			catch (error) {
				console.warn('Error while moving block', error);
			}

			// Stop drag if no target found or if the block was moved or not holding shift
			if (!hoverDragTarget.value || draggedBlock.value?.childId || !event.shiftKey) {
				markDropCompleted();
				clearActiveDrag();
			}
		}
	});

	useEventListener('keydown', (event) => {
		if (event.key !== 'Escape' || !draggedBlock.value) {
			return;
		}

		clearActiveDrag();
		resetPointerGesture();
	});

	const dragHandlerRegistry = new Map<string, OverrideBlockDragConfig>();

	return {
		dragging,
		draggedBlock,
		hoverDragTarget,
		setHoverDragTarget,
		clearHoverDragTarget,
		hoverDragTargetOwnedBy,
		hoverDragTargetContainsPoint,
		dragCapture,
		isCaptured,
		gesture: readonly(gesture),
		beginPointerGesture,
		cancelPointerGesture,
		consumeSuppressedPointerRelease,
		consumeSuppressedClick,
		startDrag,
		tryCaptureDrag,
		updateCapture,
		releaseDragCapture,
		dragHandlerRegistry,
	};
}

export type BlockEditorDrag = ReturnType<typeof useBlockEditorDrag>;
